import { useEffect, useMemo, useState } from "react";
import {
  Outlet,
  NavLink,
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  SocietyFeatureProvider,
  useSocietyFeatures,
} from "../context/SocietyFeatureContext";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  LayoutDashboard,
  Users,
  UsersRound,
  KeyRound,
  FileText,
  Receipt,
  BarChart3,
  Database,
  ArrowDownUp,
  Megaphone,
  CalendarDays,
  FileCheck2,
  MessageSquareWarning,
  Wrench,
  Building2,
  Bell,
  HardHat,
  Shield,
  ParkingSquare,
  CalendarCheck2,
  FolderKanban,
  ClipboardList,
  GraduationCap,
  Sparkles,
  FolderOpen,
  Settings,
  ShieldCheck,
  UserRoundCheck,
  PlugZap,
  Landmark,
  History,
  FileWarning,
  Menu,
  LogOut,
  ChevronDown,
  ChevronRight,
  Search,
  Headset,
  Mail,
  Phone,
  MessageCircle,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";

const COLLAPSE_KEY = "msm_society_sidebar_collapsed";

const ACTIVE =
  "bg-gradient-to-r from-[#2563EB] to-[#1d4ed8] text-white shadow-md shadow-blue-500/30";

const SECTIONS = [
  {
    items: [{ to: "", base: "", icon: LayoutDashboard, label: "Dashboard", color: "text-[#2563EB]" }],
  },
  {
    heading: "Admin",
    items: [
      { to: "settings", base: "settings", icon: Settings, label: "Society Settings", color: "text-slate-500", perm: ["settings", "view"] },
      { to: "setup", base: "setup", icon: ShieldCheck, label: "Society Setup", color: "text-[#2563EB]", perm: ["profile", "view"] },
      { to: "setup?tab=governance&focus=1", base: "setup", icon: UsersRound, label: "Governance", color: "text-indigo-500", perm: ["governance", "view"] },
      { to: "documents", base: "documents", icon: FolderOpen, label: "Documents", color: "text-orange-500", perm: ["documents", "view"] },
      { to: "finance-preview?section=accounting", base: "finance-preview", icon: Landmark, label: "Financial Foundation", color: "text-violet-500" },
      { to: "setup?tab=access&focus=1", base: "setup", icon: KeyRound, label: "Society Access", color: "text-violet-600", perm: ["settings", "manage"] },
      {
        id: "olddata",
        icon: Database,
        label: "Old Data",
        color: "text-sky-600",
        children: [
          { to: "import-old-data", base: "import-old-data", icon: Database, label: "Import Old Data", perm: ["units", "create"] },
          { to: "import-old-data?tab=history", base: "import-old-data", icon: History, label: "Import History", perm: ["units", "view"] },
          { to: "import-old-data?tab=errors", base: "import-old-data", icon: FileWarning, label: "Import Error Reports", perm: ["units", "view"] },
        ],
      },
    ],
  },
  {
    heading: "Members & Units",
    items: [
      { to: "directory", base: "directory", icon: Users, label: "Units & Members", color: "text-amber-500" },
      { to: "resident-access", base: "resident-access", icon: UserRoundCheck, label: "Resident Access", color: "text-emerald-600", perm: ["resident_portal", "manage"] },
    ],
  },
  {
    heading: "Finance & Billing",
    items: [
      { to: "finance-preview?section=billing", base: "finance-preview", icon: FileText, label: "Billing & Charges", color: "text-emerald-600" },
      { to: "finance-preview?section=receipts", base: "finance-preview", icon: Receipt, label: "Collections & Receipts", color: "text-rose-500" },
      { to: "finance-preview?section=reports", base: "finance-preview", icon: BarChart3, label: "Member Ledger & Reports", color: "text-amber-500" },
      { to: "finance-preview?section=utilities", base: "finance-preview", icon: ArrowDownUp, label: "Import & Export", color: "text-sky-600" },
    ],
  },
  {
    heading: "Communication & Governance",
    items: [
      { to: "notices", base: "notices", icon: Megaphone, label: "Notices & Announcements", color: "text-rose-500" },
      { to: "meetings", base: "meetings", icon: CalendarDays, label: "Meetings & AGM", color: "text-[#2563EB]" },
      { to: "requests", base: "requests", icon: FileCheck2, label: "Resident Requests", color: "text-violet-600" },
      { to: "complaints", base: "complaints", icon: MessageSquareWarning, label: "Complaints & Helpdesk", color: "text-emerald-600" },
    ],
  },
  {
    heading: "Operations & Facilities",
    items: [
      {
        id: "operations",
        icon: Wrench,
        label: "Operations & Maintenance",
        color: "text-orange-500",
        children: [
          { to: "assets", base: "assets", icon: Building2, label: "Assets & Inventory" },
          { to: "amc", base: "amc", icon: Bell, label: "AMC & Contracts" },
          { to: "staff", base: "staff", icon: HardHat, label: "Staff & Workforce" },
        ],
      },
      {
        id: "security",
        icon: Shield,
        label: "Security & Facilities",
        color: "text-emerald-600",
        children: [
          { to: "gate-security", base: "gate-security", icon: Shield, label: "Gate & Security" },
          { to: "parking", base: "parking", icon: ParkingSquare, label: "Parking Management" },
          { to: "amenities", base: "amenities", icon: CalendarCheck2, label: "Amenities & Bookings" },
        ],
      },
      {
        id: "services",
        icon: FolderKanban,
        label: "Services & Projects",
        color: "text-sky-600",
        children: [
          { to: "requirements", base: "requirements", icon: ClipboardList, label: "Requirements" },
          { to: "projects", base: "projects", icon: FolderKanban, label: "Projects" },
          { to: "experts", base: "experts", icon: GraduationCap, label: "Expert Consultations" },
        ],
      },
    ],
  },
  {
    heading: "Tools & Resources",
    items: [
      { to: "ask", base: "ask", icon: Sparkles, label: "Ask SocietyMitra AI", color: "text-emerald-600", badge: "New", accent: true },
      {
        id: "tools",
        icon: FolderOpen,
        label: "Society Tools",
        color: "text-orange-500",
        children: [
          { to: "tools", base: "tools", icon: Wrench, label: "Free Society Tools" },
          { to: "letterhead", base: "letterhead", icon: FileText, label: "Society Letterhead", perm: ["profile", "view"] },
        ],
      },
      { to: "integrations", base: "integrations", icon: PlugZap, label: "Sender Integrations", color: "text-slate-500", perm: ["settings", "manage"] },
    ],
  },
];

const FEATURE_NAV = {
  amc: "sm07_amc_contracts",
  assets: "sm06_assets_inventory",
  complaints: "sm05_helpdesk",
  directory: "sm02_directory",
  staff: "sm08_staff_workforce",
  meetings: "rp06_meetings",
  requests: "rp07_formal_requests",
  "gate-security": "rp08_gate_security",
  amenities: "rp09_amenity_booking",
  parking: "rp10_parking_vehicles",
};

const PERMISSION_NAV = {
  amenities: ["amenities", "view"],
  assets: ["assets", "view"],
  complaints: ["helpdesk", "view"],
  directory: ["member_master", "view"],
  documents: ["documents", "view"],
  "finance-preview": ["finance_billing", "view"],
  "gate-security": ["gate_security", "view"],
  integrations: ["settings", "manage"],
  letterhead: ["profile", "view"],
  meetings: ["meetings", "view"],
  notices: ["resident_portal", "manage"],
  parking: ["parking", "view"],
  "resident-access": ["resident_portal", "manage"],
  requests: ["resident_requests", "view"],
  setup: ["profile", "view"],
  settings: ["settings", "view"],
  staff: ["staff", "view"],
};

function BrandHeader() {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="society-brand-header">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-black/5">
        <img src="/logo-icon.png" alt="MySocietyMitra" className="h-9 w-9 object-contain" />
      </div>
      <div className="leading-tight">
        <div className="font-display text-[17px] font-bold tracking-tight text-[#0F2B63]">
          MySociety<span className="text-[#2563EB]">Mitra</span>
        </div>
        <div className="text-[10px] text-slate-500">by Shivkrupa Enterprises</div>
        <div className="text-[10px] font-medium text-[#F97316]">Apka Society Mitra</div>
      </div>
    </Link>
  );
}

function SocietyCard({ society, planLabel }) {
  const name = society?.name || "Loading society…";
  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F2B63] via-[#143a7a] to-[#2563EB] p-4 text-white shadow-lg shadow-blue-900/20"
      data-testid="society-info-card"
    >
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10" />
      <div className="relative flex items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white/15 ring-1 ring-white/25">
          {society?.logo ? (
            <img src={society.logo} alt="" className="h-full w-full object-cover" />
          ) : (
            <Building2 className="h-6 w-6 text-white" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold leading-snug" title={name} data-testid="society-card-name">
            {name}
          </div>
          <span
            className="mt-1 inline-flex items-center rounded-full bg-emerald-400/90 px-2 py-0.5 text-[10px] font-semibold text-emerald-950"
            data-testid="society-plan-badge"
          >
            {planLabel}
          </span>
        </div>
      </div>
    </div>
  );
}

function SupportCard() {
  return (
    <div className="mt-2 rounded-2xl border border-slate-200 bg-[#F5F8FD] p-4" data-testid="society-support-card">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2563EB]/10 text-[#2563EB]">
          <Headset className="h-5 w-5" />
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold text-[#0F2B63]">Need Help?</div>
          <div className="text-[11px] text-slate-500">Contact Support</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <a href="mailto:info@mysocietymitra.com" className="flex flex-col items-center gap-1 rounded-lg bg-white py-2 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200 transition hover:text-[#2563EB]" data-testid="support-email">
          <Mail className="h-4 w-4 text-[#2563EB]" /> Email
        </a>
        <a href="tel:+917567569060" className="flex flex-col items-center gap-1 rounded-lg bg-white py-2 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200 transition hover:text-[#2563EB]" data-testid="support-call">
          <Phone className="h-4 w-4 text-emerald-600" /> Call
        </a>
        <a href="https://wa.me/918080220880" target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 rounded-lg bg-white py-2 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200 transition hover:text-[#2563EB]" data-testid="support-whatsapp">
          <MessageCircle className="h-4 w-4 text-emerald-500" /> WhatsApp
        </a>
      </div>
    </div>
  );
}

function SideNav({ onNav, user, collapsed = false, onExpandSidebar }) {
  const { byKey, loading } = useSocietyFeatures();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [permissions, setPermissions] = useState(null);
  const [search, setSearch] = useState("");
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("msm_society_nav_open") || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    api
      .get("/society-setup/context")
      .then((response) => setPermissions(response.data.permissions || {}))
      .catch(() => setPermissions({}));
  }, []);

  const hasPermission = (item) => {
    const requirement = item.perm || PERMISSION_NAV[item.base];
    if (!requirement || permissions === null) return true;
    const [module, action] = requirement;
    return Boolean(permissions[module]?.includes(action));
  };
  const isVisible = (item) => {
    if (user?.role === "society_portal") return item.base === "setup";
    const featureKey = item.feature || FEATURE_NAV[item.base];
    const featureAllowed = !featureKey || (!loading && byKey(featureKey)?.allowed);
    return featureAllowed && hasPermission(item);
  };

  const isActive = (item) => {
    const [path, query] = (item.to || "").split("?");
    if (location.pathname !== `/app/${path}`) return false;
    if (!query) return true;
    const want = new URLSearchParams(query);
    for (const [key, value] of want) {
      if (searchParams.get(key) !== value) return false;
    }
    return true;
  };

  const openGroup = (id) => {
    setOpenGroups((current) => {
      const next = { ...current, [id]: true };
      sessionStorage.setItem("msm_society_nav_open", JSON.stringify(next));
      return next;
    });
  };
  const toggleGroup = (id) => {
    setOpenGroups((current) => {
      const next = { ...current, [id]: !current[id] };
      sessionStorage.setItem("msm_society_nav_open", JSON.stringify(next));
      return next;
    });
  };

  const visibleSections = useMemo(
    () =>
      SECTIONS.map((section) => ({
        ...section,
        items: section.items.reduce((acc, item) => {
          if (!item.children) return isVisible(item) ? [...acc, item] : acc;
          const children = item.children.filter(isVisible);
          return children.length ? [...acc, { ...item, children }] : acc;
        }, []),
      })).filter((section) => section.items.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissions, loading, byKey, user],
  );

  const searchResults = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return null;
    const flat = [];
    visibleSections.forEach((section) =>
      section.items.forEach((item) => {
        if (item.children) {
          item.children.forEach((child) => {
            if (child.label.toLowerCase().includes(term)) flat.push(child);
          });
        } else if (item.label.toLowerCase().includes(term)) {
          flat.push(item);
        }
      }),
    );
    return flat;
  }, [search, visibleSections]);

  const testId = (item) =>
    `society-nav-${item.base || "dashboard"}${item.to.includes("?") ? "-" + item.to.split("=").pop() : ""}`;

  // Compact (icon-only) rendering
  if (collapsed) {
    const leaves = [];
    visibleSections.forEach((section) =>
      section.items.forEach((item) => {
        if (item.children) {
          leaves.push({ group: true, ...item });
        } else {
          leaves.push(item);
        }
      }),
    );
    return (
      <TooltipProvider delayDuration={0}>
        <nav className="flex flex-col items-center gap-1.5 p-2" data-testid="society-sidebar-navigation">
          {leaves.map((item) => {
            const Icon = item.icon;
            if (item.group) {
              const childActive = item.children.some(isActive);
              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => {
                        openGroup(item.id);
                        onExpandSidebar?.();
                      }}
                      data-testid={`society-nav-group-collapsed-${item.id}`}
                      className={`flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-200 ${
                        childActive ? ACTIVE : "text-slate-500 hover:bg-[#2563EB]/5 hover:text-[#0F2B63]"
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${childActive ? "text-white" : item.color || "text-slate-400"}`} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              );
            }
            const active = isActive(item);
            return (
              <Tooltip key={item.to}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={`/app/${item.to}`}
                    onClick={onNav}
                    data-testid={testId(item)}
                    className={`flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-200 ${
                      active ? ACTIVE : "text-slate-500 hover:bg-[#2563EB]/5 hover:text-[#0F2B63]"
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${active ? "text-white" : item.color || "text-slate-400"}`} />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            );
          })}
          <div className="my-2 h-px w-8 bg-slate-200" />
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href="mailto:info@mysocietymitra.com"
                className="flex h-11 w-11 items-center justify-center rounded-xl text-[#2563EB] hover:bg-[#2563EB]/5"
                data-testid="society-support-collapsed"
              >
                <Headset className="h-5 w-5" />
              </a>
            </TooltipTrigger>
            <TooltipContent side="right">Need help? Contact support</TooltipContent>
          </Tooltip>
        </nav>
      </TooltipProvider>
    );
  }

  const Leaf = ({ item, nested }) => {
    const Icon = item.icon;
    const active = isActive(item);
    return (
      <NavLink
        to={`/app/${item.to}`}
        onClick={onNav}
        data-testid={testId(item)}
        className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
          active ? ACTIVE : "text-slate-600 hover:bg-[#2563EB]/5 hover:text-[#0F2B63]"
        } ${nested ? "py-2 text-[13px]" : ""}`}
      >
        <Icon className={`h-[18px] w-[18px] shrink-0 transition-colors ${active ? "text-white" : item.color || "text-slate-400"}`} />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.badge && !active && (
          <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700">{item.badge}</span>
        )}
        {!nested && !item.badge && (
          <ChevronRight className={`h-4 w-4 shrink-0 ${active ? "text-white/80" : "text-slate-300"}`} />
        )}
      </NavLink>
    );
  };

  return (
    <nav className="flex flex-col gap-1 p-3" data-testid="society-sidebar-navigation">
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search menu…"
          className="h-10 rounded-xl border-slate-200 bg-[#F5F8FD] pl-9 text-sm"
          data-testid="society-nav-search"
        />
      </div>

      {searchResults ? (
        <div className="flex flex-col gap-1" data-testid="society-nav-search-results">
          {searchResults.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-slate-400">No matching menu items</p>
          ) : (
            searchResults.map((item) => <Leaf key={item.to} item={item} />)
          )}
        </div>
      ) : (
        visibleSections.map((section, index) => (
          <div key={section.heading || `section-${index}`} className="mb-1">
            {section.heading && (
              <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {section.heading}
              </div>
            )}
            {section.items.map((item) => {
              if (!item.children) return <Leaf key={item.to} item={item} />;
              const childActive = item.children.some(isActive);
              const expanded = Boolean(openGroups[item.id] || childActive);
              const Icon = item.icon;
              return (
                <div key={item.id} data-testid={`society-nav-group-${item.id}`}>
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => toggleGroup(item.id)}
                    data-testid={`society-nav-group-toggle-${item.id}`}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-all duration-200 ${
                      childActive ? "bg-[#2563EB]/5 text-[#0F2B63]" : "text-slate-600 hover:bg-[#2563EB]/5 hover:text-[#0F2B63]"
                    }`}
                  >
                    <Icon className={`h-[18px] w-[18px] shrink-0 ${item.color || "text-slate-400"}`} />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    <ChevronRight className={`h-4 w-4 shrink-0 text-slate-300 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`} />
                  </button>
                  {expanded && (
                    <div className="ml-4 mt-1 flex flex-col gap-0.5 border-l border-slate-200 pl-2">
                      {item.children.map((child) => (
                        <Leaf key={child.to} item={child} nested />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}

      {user?.role === "society_portal" && (
        <p className="mt-3 border-t px-3 pt-3 text-xs leading-relaxed text-slate-500" data-testid="society-nav-access-note">
          You have <b>Society Setup</b> access. Members, Finance, Accounting and other
          modules appear here once a Society administrator grants you the matching role.
        </p>
      )}

      <SupportCard />
    </nav>
  );
}

export default function PortalLayout() {
  return (
    <SocietyFeatureProvider>
      <PortalShell />
    </SocietyFeatureProvider>
  );
}

function PortalShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [society, setSociety] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [sub, setSub] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");

  useEffect(() => {
    api.get("/societies/me").then((r) => setSociety(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    api.get("/subscription/me").then((r) => setSub(r.data)).catch(() => {});
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      return next;
    });
  };

  const unread = notifs.filter((n) => !n.read).length;
  const planLabel = sub
    ? Number(sub.total_monthly) > 0 && sub.effective_status !== "trial"
      ? "Paid Plan"
      : sub.effective_status === "trial"
        ? "Trial Plan"
        : "Free Plan"
    : "Free Plan";

  return (
    <div className="min-h-screen bg-[#F5F8FD]">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="flex h-16 items-center justify-between gap-4 px-4">
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <button className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden" data-testid="portal-menu-toggle">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 overflow-y-auto p-0">
                <SheetTitle className="sr-only">Society navigation</SheetTitle>
                <div className="border-b border-slate-100 p-4">
                  <BrandHeader />
                  <div className="mt-4">
                    <SocietyCard society={society} planLabel={planLabel} />
                  </div>
                </div>
                <SideNav onNav={() => {}} user={user} collapsed={false} />
              </SheetContent>
            </Sheet>
            <div className="hidden sm:block">
              <div className="font-display text-lg font-bold text-[#0F2B63]">Society Admin</div>
              <div className="text-xs text-slate-500">Manage your society easily with MySocietyMitra</div>
            </div>
            <div className="sm:hidden">
              <BrandHeader />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative rounded-xl p-2 text-slate-600 hover:bg-slate-100" data-testid="notif-bell">
                  <Bell className="h-5 w-5" />
                  {unread > 0 && (
                    <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#F43F5E] text-[10px] font-semibold text-white">
                      {unread}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="px-3 py-2 text-sm font-semibold">Notifications</div>
                <DropdownMenuSeparator />
                {notifs.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications</div>
                ) : (
                  notifs.slice(0, 8).map((n) => (
                    <div key={n.notification_id} className="px-3 py-2 text-sm">
                      <div className="font-medium">{n.title}</div>
                      <div className="text-xs text-muted-foreground">{n.message}</div>
                    </div>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-slate-100" data-testid="user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <ChevronDown className="hidden h-4 w-4 text-slate-400 sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/app/settings")}>Society Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {society &&
        society.verification_status &&
        !["APPROVED", "ACTIVATED"].includes(society.verification_status) && (
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-2.5 text-center text-sm text-amber-800" data-testid="society-pending-banner">
            Your society is registered on a <b>trial</b> and pending Super Admin approval. You have full access to set up your workspace meanwhile.
          </div>
        )}

      <div className="flex">
        <aside
          className={`sticky top-16 hidden h-[calc(100vh-4rem)] shrink-0 overflow-y-auto border-r border-slate-200 bg-white transition-all duration-200 lg:block ${
            collapsed ? "w-[76px]" : "w-[280px]"
          }`}
          data-testid="society-desktop-sidebar"
          data-collapsed={collapsed ? "true" : "false"}
        >
          <div className={`border-b border-slate-100 ${collapsed ? "px-2 py-3" : "p-4"}`}>
            {collapsed ? (
              <div className="flex flex-col items-center gap-3">
                <Link to="/" className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-black/5" data-testid="society-brand-header">
                  <img src="/logo-icon.png" alt="MySocietyMitra" className="h-9 w-9 object-contain" />
                </Link>
                <div
                  className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-[#0F2B63] to-[#2563EB] ring-1 ring-white/20"
                  title={society?.name || ""}
                  data-testid="society-info-card"
                >
                  {society?.logo ? (
                    <img src={society.logo} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Building2 className="h-5 w-5 text-white" />
                  )}
                </div>
                <button
                  onClick={toggleCollapsed}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-[#2563EB]/5 hover:text-[#2563EB]"
                  data-testid="sidebar-collapse-toggle"
                  aria-label="Expand sidebar"
                >
                  <PanelLeftOpen className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between">
                  <BrandHeader />
                  <button
                    onClick={toggleCollapsed}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-[#2563EB]/5 hover:text-[#2563EB]"
                    data-testid="sidebar-collapse-toggle"
                    aria-label="Collapse sidebar"
                  >
                    <PanelLeftClose className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-4">
                  <SocietyCard society={society} planLabel={planLabel} />
                </div>
              </>
            )}
          </div>
          <SideNav user={user} collapsed={collapsed} onExpandSidebar={() => { setCollapsed(false); localStorage.setItem(COLLAPSE_KEY, "0"); }} />
        </aside>
        <main className="max-w-full flex-1 overflow-x-hidden p-5 md:p-8">
          <Outlet context={{ society }} />
        </main>
      </div>
    </div>
  );
}
